import React, { useState, useEffect, useRef } from 'react';
import { format, parse, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

/**
 * Componente personalizado de input de data com seletor de calendário
 * Exibe e aceita datas no formato brasileiro (DD/MM/AAAA)
 * Compatível com a versão anterior, mas com funcionalidades adicionais
 * 
 * @param {Object} props - Propriedades do componente
 * @param {string} props.id - ID do campo
 * @param {string} props.name - Nome do campo para formulários
 * @param {Date|string} props.value - Valor da data (string ISO ou objeto Date)
 * @param {Function} props.onChange - Função chamada quando a data muda
 * @param {string} props.className - Classes adicionais para o input
 * @param {boolean} props.required - Se o campo é obrigatório
 * @param {string} props.label - Rótulo do campo
 * @param {string} props.labelClassName - Classes adicionais para o rótulo
 * @param {boolean} props.fullWidth - Se o campo deve ocupar toda a largura
 * @param {string} props.datePlacement - Posição do calendário ('bottom', 'top', 'left', 'right')
 * @param {boolean} props.showCalendar - Nova prop para controlar se o seletor de calendário deve ser exibido
 * @returns {JSX.Element} - Elemento React
 */
const DateInput = ({ 
  id, 
  name, 
  value, 
  onChange, 
  className = '', 
  required = false, 
  label, 
  labelClassName = '', 
  fullWidth = true,
  datePlacement = 'bottom',
  showCalendar = true
}) => {
  // Estado para armazenar o valor formatado exibido para o usuário
  const [displayValue, setDisplayValue] = useState('');
  // Estado para controlar a exibição do seletor de data
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Referência para o componente para fechar o seletor quando clicar fora
  const componentRef = useRef(null);
  
  // Efeito para fechar o seletor quando clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (componentRef.current && !componentRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [componentRef]);

  // Efeito para atualizar o valor exibido quando o valor real muda
  useEffect(() => {
    if (value) {
      try {
        // Converter o valor para Date se for string
        let dateValue;
        if (typeof value === 'string') {
          // Tratar formato ISO (YYYY-MM-DD) ou BR (DD/MM/YYYY)
          if (value.includes('-')) {
            // ISO
            dateValue = new Date(value.includes('T') ? value : `${value}T00:00:00`);
          } else if (value.includes('/')) {
            // BR
            dateValue = parse(value, 'dd/MM/yyyy', new Date(), { locale: ptBR });
          } else {
            dateValue = new Date(value);
          }
        } else if (value instanceof Date) {
          dateValue = value;
        }
        
        // Verificar se a data é válida
        if (dateValue && isValid(dateValue)) {
          setDisplayValue(format(dateValue, 'dd/MM/yyyy', { locale: ptBR }));
        }
      } catch (error) {
        console.error('Erro ao formatar data:', error);
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  // Função para lidar com a mudança no input
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);

    // Validar o formato da data (DD/MM/AAAA)
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(inputValue)) {
      try {
        // Parse a data de DD/MM/AAAA para objeto Date
        const parsedDate = parse(inputValue, 'dd/MM/yyyy', new Date(), { locale: ptBR });
        
        // Se a data for válida, converter para formato ISO e chamar o onChange
        if (isValid(parsedDate)) {
          // Para compatibilidade com a implementação antiga, retornamos formato ISO como string
          const isoDate = format(parsedDate, 'yyyy-MM-dd');
          
          if (onChange) {
            // Simular evento para manter compatibilidade com handlers existentes
            onChange({
              target: {
                name: name || id,
                value: isoDate
              }
            });
          }
        }
      } catch (error) {
        console.error('Erro ao analisar data:', error);
      }
    }
  };
  
  // Função para lidar com a seleção de data no calendário
  const handleDateChange = (date) => {
    if (onChange && date && isValid(date)) {
      try {
        // Para compatibilidade com a implementação antiga, retornamos formato ISO como string
        const isoDate = format(date, 'yyyy-MM-dd');
        
        // Atualizar o display value
        setDisplayValue(format(date, 'dd/MM/yyyy', { locale: ptBR }));
        
        // Simular evento para manter compatibilidade com handlers existentes
        onChange({
          target: {
            name: name || id,
            value: isoDate // Sempre retornamos string no formato ISO
          }
        });
      } catch (error) {
        console.error('Erro ao processar data:', error);
      }
    }
    
    setShowDatePicker(false);
  };
  
  // Determinar a posição do calendário
  const getPickerPosition = () => {
    switch(datePlacement) {
      case 'top': return 'bottom-full mb-2';
      case 'right': return 'left-full ml-2';
      case 'left': return 'right-full mr-2';
      case 'bottom':
      default: return 'top-full mt-2';
    }
  };

  return (
    <div 
      className={`${fullWidth ? 'w-full' : ''} relative`} 
      ref={componentRef}
    >
      {label && (
        <label 
          htmlFor={id} 
          className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        <input
          type="text"
          id={id}
          name={name || id}
          value={displayValue}
          onChange={handleInputChange}
          onClick={() => showCalendar && setShowDatePicker(true)}
          placeholder="DD/MM/AAAA"
          className={`px-3 py-2 ${showCalendar ? 'pr-10' : ''} border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${fullWidth ? 'w-full' : ''} ${className}`}
          required={required}
          maxLength={10}
          pattern="\d{2}/\d{2}/\d{4}"
        />
        
        {showCalendar && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 px-3 flex items-center bg-transparent border-0"
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
        )}
      </div>
      
      {showCalendar && showDatePicker && (
        <div className={`absolute z-10 ${getPickerPosition()} bg-white border border-gray-300 rounded-md shadow-lg`}>
          <DayPicker
            locale={ptBR}
            mode="single"
            selected={value ? (typeof value === 'string' ? new Date(value.includes('T') ? value : `${value}T00:00:00`) : value) : undefined}
            onSelect={(date) => {
              if (date) {
                handleDateChange(date);
              }
            }}
            weekStartsOn={0} // Domingo
            classNames={{
              caption: 'flex justify-center py-2 mb-4',
              caption_label: 'text-sm font-medium text-gray-900',
              nav: 'flex items-center',
              nav_button: 'h-6 w-6 bg-transparent p-0 flex items-center justify-center',
              nav_button_previous: 'absolute left-2',
              nav_button_next: 'absolute right-2',
              table: 'w-full border-collapse',
              head_row: 'flex',
              head_cell: 'w-9 h-9 flex items-center justify-center text-xs text-gray-500',
              row: 'flex w-full mt-1',
              cell: 'w-9 h-9 relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
              day: 'h-9 w-9 p-0 flex items-center justify-center rounded-full hover:bg-blue-100',
              day_selected: 'bg-blue-500 text-white hover:bg-blue-600',
              day_today: 'font-bold',
              day_outside: 'text-gray-300',
              day_disabled: 'text-gray-300',
              day_hidden: 'invisible'
            }}
            formatters={{
              formatWeekdayName: (weekday) => ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][weekday.getDay()],
              formatMonthCaption: (date) => [
                'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
              ][date.getMonth()] + ' ' + date.getFullYear()
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DateInput; 